<template>
  <Teleport to="body">
<div class="modal fade" :id="modalId" tabindex="-1" ref="modalEl">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title mb-0">Gerar orçamento</h5>
            <span class="text-muted fs-7">Vincule item e valor aos procedimentos planejados</span>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
        </div>

        <form @submit.prevent="submit">
          <div class="modal-body">
            <div class="mb-4">
              <label class="form-label required">Forma de pagamento</label>
              <select class="form-select form-select-solid" v-model="form.id_paymeth" required>
                <option :value="null">Selecione</option>
                <option v-for="method in paymentMethods" :key="method.id_paymeth" :value="method.id_paymeth">
                  {{ method.paymeth_name }}
                </option>
              </select>
            </div>

            <div class="mb-4" v-if="isInsurancePayment">
              <label class="form-label required">Convênio</label>
              <select class="form-select form-select-solid" v-model="form.id_insurance" required>
                <option :value="null">Selecione</option>
                <option v-for="insurance in insurances" :key="insurance.id_insurance" :value="insurance.id_insurance">
                  {{ insuranceLabel(insurance) }}
                </option>
              </select>
            </div>

            <div class="table-responsive" v-if="lines.length">
              <table class="table table-row-bordered align-middle mb-0">
                <thead>
                <tr class="fw-bold text-muted fs-7">
                  <th>Procedimento</th>
                  <th class="w-250px">Item do catálogo</th>
                  <th class="w-120px">Valor</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="line in lines" :key="line.id_procedure">
                  <td>
                    <div class="fw-semibold fs-7">{{ line.procedure_name }}</div>
                    <div class="text-muted fs-8">Dente {{ line.tooth }}</div>
                  </td>
                  <td>
                    <InputSearchCatalogItem v-model="line.id_item"
                                            :display-value="line.item_label"
                                            size="sm"
                                            placeholder="Buscar item"
                                            @select="(item) => selectCatalogItem(line, item)"
                                            @clear="line.item_label = ''"/>
                  </td>
                  <td>
                    <input class="form-control form-control-sm form-control-solid"
                           type="number"
                           min="0.01"
                           step="0.01"
                           v-model.number="line.procedure_value">
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <div class="notice bg-light-warning rounded border-warning border border-dashed p-4" v-else>
              Não há procedimentos planejados para orçar.
            </div>

            <div class="mt-4">
              <label class="form-label">Observação</label>
              <textarea class="form-control form-control-solid" rows="2" v-model="form.budget_obs"></textarea>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <span class="d-inline-block"
                  tabindex="0"
                  :title="submitTooltip || undefined">
              <button type="submit" class="btn btn-primary" :disabled="!canSubmit">
                {{ saving ? 'Gerando...' : 'Gerar orçamento' }}
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import http from '@/helpers/http';
import Alert from '@/helpers/alert';
import InputSearchCatalogItem from '@/components/InputSearchCatalogItem.vue';

export default {
  name: 'OdontogramBudgetModal',
  components: { InputSearchCatalogItem },
  props: {
    modalId: { type: String, required: true },
    treatment: { type: Object, default: null },
    treatmentsUrl: { type: String, required: true },
  },
  emits: ['generated'],
  data() {
    return {
      paymentMethods: [],
      insurances: [],
      form: {
        id_paymeth: null,
        id_insurance: null,
        budget_obs: '',
      },
      lines: [],
      saving: false,
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    isInsurancePayment() {
      return !!this.paymentMethods.find((method) => method.id_paymeth === this.form.id_paymeth)?.is_insurance;
    },
    canSubmit() {
      if (this.saving || !this.lines.length || !this.form.id_paymeth) {
        return false;
      }

      if (this.isInsurancePayment && !this.form.id_insurance) {
        return false;
      }

      return !this.lines.some((line) => !line.id_item || !line.procedure_value || line.procedure_value <= 0);
    },
    submitTooltip() {
      if (this.saving || this.canSubmit) {
        return '';
      }

      if (!this.lines.length) {
        return 'Não há procedimentos planejados para orçar.';
      }

      if (!this.form.id_paymeth) {
        return 'Selecione a forma de pagamento.';
      }

      if (this.isInsurancePayment && !this.form.id_insurance) {
        return 'Selecione o convênio.';
      }

      return 'Todos os procedimentos precisam de item e valor.';
    },
  },
  watch: {
    'form.id_paymeth'(value, previous) {
      if (previous == null || previous === value || this.isInsurancePayment) {
        return;
      }

      this.form.id_insurance = null;
    },
  },
  methods: {
    open() {
      this.reset();
      this.loadOptions();
      window.bootstrap.Modal.getOrCreateInstance(this.$refs.modalEl).show();
    },

    reset() {
      this.form = { id_paymeth: null, id_insurance: null, budget_obs: '' };
      this.lines = (this.treatment?.procedures ?? [])
        .filter((procedure) => procedure.status === 'planned')
        .map((procedure) => ({
          id_procedure: procedure.id_procedure,
          procedure_name: procedure.procedure_name,
          tooth: procedure.tooth,
          id_item: procedure.id_item ?? null,
          item_label: procedure.item?.item_name ?? '',
          procedure_value: procedure.procedure_value ?? null,
        }));
    },

    loadOptions() {
      if (!this.place?.id_place) {
        return;
      }

      http.get(`/places/${this.place.id_place}/payment-methods`).then((response) => {
        this.paymentMethods = response.data ?? [];
      });
      http.get(`/places/${this.place.id_place}/insurances`).then((response) => {
        this.insurances = response.data?.data ?? response.data ?? [];
      });
    },

    insuranceLabel(insurance) {
      if (insurance.trading_name) {
        return `${insurance.trading_name} - ${insurance.insurance_name}`;
      }

      return insurance.insurance_name;
    },

    selectCatalogItem(line, item) {
      line.item_label = item.item_name ?? '';
      if (!line.procedure_value) {
        line.procedure_value = Number(item.item_value ?? item.value ?? item.active_value?.item_value ?? 0) || null;
      }
    },

    async submit() {
      if (!this.canSubmit) {
        return;
      }

      this.saving = true;
      const base = `${this.treatmentsUrl}/${this.treatment.id_treatment}`;

      try {
        for (const line of this.lines) {
          await http.put(`${base}/procedures/${line.id_procedure}`, {
            id_item: line.id_item,
            procedure_value: line.procedure_value,
          });
        }

        const response = await http.post(`${base}/budget`, {
          id_paymeth: this.form.id_paymeth,
          id_insurance: this.isInsurancePayment ? this.form.id_insurance : null,
          budget_obs: this.form.budget_obs || null,
        });

        Alert.toastSuccess('Orçamento gerado com sucesso.');
        this.$emit('generated', response.data);
        window.bootstrap.Modal.getOrCreateInstance(this.$refs.modalEl).hide();
      } catch (error) {
        const message = error.response?.data?.message ?? 'Erro ao gerar orçamento.';
        Alert.toastError(message);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
